import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { mapConfigs, mapImageMappings } from "../../content/maps/mapData";
import { WEAPON_NAMES } from "../MatchPage/Replay/replayParser";
import { WrappedPositions, WrappedPositionCell } from "../../services/WrappedService";
import styles from "./Wrapped.module.css";

/**
 * The Wrapped 3D positional object — orbitable, multi-layer, inspectable.
 *
 * Cells arrive pre-aggregated from wrapped_positions as (layer, kind, x, y, count), where
 * `kind` is the weapon id for kill/death/victim layers and the replay packet type for utility.
 * That is what lets a hovered pillar break down into "AK-47 x7, AWP x3" or "Smoke x4, Flash x2".
 *
 * The radar is drawn as the floor through the SAME linear transform as the cells: mapConfigs
 * maps world -> radar space (0..1024) and the projection is linear in that space, so a 2x3
 * canvas matrix lands the image exactly under the pillars with no alignment fudge.
 *
 * No three.js. The projection is parameterised by yaw + elevation, so orbiting re-projects and
 * re-sorts; lighting is faked with per-face gradients keyed off each face's screen-space normal.
 */

const W = 1200;
const H = 860;
const RADAR = 1024;

/** Utility packet types, straight from the replay format. */
const UTIL_KIND: Record<number, string> = { 3: "HE", 4: "Flash", 5: "Smoke", 7: "Molotov" };

/**
 * Sub-filters within a layer. Utility filters by its four packet types; weapon layers filter
 * by class rather than individual gun — ~20 chips would be unusable, and "snipers" is the
 * question people actually ask, not "SSG 08 specifically".
 * Ids match WEAPON_NAMES in the replay parser.
 */
type KindGroup = { key: string; ids: number[] };

const WEAPON_GROUPS: KindGroup[] = [
  { key: "rifle", ids: [20, 21, 22, 23, 24, 25, 26] },
  { key: "sniper", ids: [30, 31, 32, 33] },
  { key: "smg", ids: [11, 12, 13, 14, 15, 16, 17] },
  { key: "pistol", ids: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] },
  { key: "shotgun", ids: [40, 41, 42, 43] },
  { key: "lmg", ids: [50, 51] },
  { key: "nade", ids: [70, 71, 72, 73, 74, 75, 80] },
  { key: "melee", ids: [60, 61] },
];

const UTIL_GROUPS: KindGroup[] = [
  { key: "smoke", ids: [5] },
  { key: "flash", ids: [4] },
  { key: "he", ids: [3] },
  { key: "molotov", ids: [7] },
];

const groupsFor = (layerId: number) => (layerId === 4 ? UTIL_GROUPS : WEAPON_GROUPS);

const groupOf = (layerId: number, kind: number): string | undefined =>
  groupsFor(layerId).find((g) => g.ids.includes(kind))?.key;

export const LAYERS = [
  { id: 0, key: "kills", lo: [247, 226, 186] as const, rgb: [226, 88, 40] as const },
  { id: 1, key: "deaths", lo: [242, 198, 198] as const, rgb: [198, 38, 50] as const },
  { id: 2, key: "victims", lo: [206, 224, 245] as const, rgb: [56, 122, 214] as const },
  { id: 4, key: "utility", lo: [222, 214, 248] as const, rgb: [130, 96, 232] as const },
  { id: 5, key: "plants", lo: [246, 235, 190] as const, rgb: [214, 168, 22] as const },
];

const LAYER_BY_ID = new Map(LAYERS.map((l) => [l.id, l]));

const mix = (a: readonly number[], b: readonly number[], t: number): [number, number, number] => [
  Math.round(a[0] + (b[0] - a[0]) * t),
  Math.round(a[1] + (b[1] - a[1]) * t),
  Math.round(a[2] + (b[2] - a[2]) * t),
];

const short = (m: string) => m.replace(/^de_/, "").replace(/^\w/, (c) => c.toUpperCase());

type Pt = { x: number; y: number };

/** One rendered pillar: a cell of one layer, with its per-kind breakdown retained. */
type Pillar = {
  layerId: number;
  cx: number;
  cy: number;
  count: number;
  kinds: { kind: number; count: number }[];
  screenTop: Pt[];
  screenBase: Pt[];
  depth: number;
  rgb: readonly number[];
  t: number;
};

type CellGroup = {
  layerId: number;
  cx: number;
  cy: number;
  count: number;
  kinds: Map<number, number>;
};

const inPoly = (p: Pt, poly: Pt[]): boolean => {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i], b = poly[j];
    if (((a.y > p.y) !== (b.y > p.y)) && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) {
      inside = !inside;
    }
  }
  return inside;
};

const WrappedPositionMap = ({ positions }: { positions: WrappedPositions }) => {
  const { t } = useTranslation("pages");
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pillarsRef = useRef<Pillar[]>([]);

  const renderable = useMemo(
    () => positions.maps.filter((m) => mapConfigs[m.mapName] && mapImageMappings[m.mapName]),
    [positions]
  );

  const [mapIdx, setMapIdx] = useState(0);
  const [yaw, setYaw] = useState(0.62);
  const [elev, setElev] = useState(0.52);
  const [zoom, setZoom] = useState(1);
  const [active, setActive] = useState<number[]>([0, 1]);
  const [radar, setRadar] = useState<HTMLCanvasElement | null>(null);
  const [hover, setHover] = useState<Pillar | null>(null);
  const [pinned, setPinned] = useState<Pillar | null>(null);
  /** layerId -> selected group keys. Empty/absent means "all kinds". */
  const [kindFilter, setKindFilter] = useState<Record<number, string[]>>({});
  const drag = useRef<{ x: number; y: number; moved: boolean } | null>(null);
  const hoverRaf = useRef(0);

  const current = renderable[Math.min(mapIdx, Math.max(0, renderable.length - 1))];
  const config = current ? mapConfigs[current.mapName] : undefined;

  // Bake the radar ONCE per map with its grayscale/brightness filter already applied.
  // ctx.filter is extremely slow; running it inside the draw loop meant every mouse move
  // re-filtered a 1024x1024 image, which is what made hovering feel glitchy.
  useEffect(() => {
    if (!current) return;
    const src = mapImageMappings[current.mapName];
    if (!src) { setRadar(null); return; }
    const img = new Image();
    let cancelled = false;
    img.onload = () => {
      if (cancelled) return;
      const off = document.createElement("canvas");
      off.width = RADAR;
      off.height = RADAR;
      const octx = off.getContext("2d");
      if (!octx) { setRadar(null); return; }
      octx.filter = "grayscale(1) brightness(1.25) contrast(1.05)";
      octx.drawImage(img, 0, 0, RADAR, RADAR);
      setRadar(off);
    };
    img.onerror = () => { if (!cancelled) setRadar(null); };
    img.src = src;
    return () => { cancelled = true; };
  }, [current]);

  // Collapse (layer, kind, x, y) rows into one entry per (layer, x, y), keeping the kind split
  // so a pillar can be broken down on inspect.
  const grouped = useMemo(() => {
    const m = new Map<string, CellGroup>();
    current?.cells.forEach((c: WrappedPositionCell) => {
      const k = `${c.layer}:${c.x}:${c.y}`;
      let e = m.get(k);
      if (!e) { e = { layerId: c.layer, cx: c.x, cy: c.y, count: 0, kinds: new Map() }; m.set(k, e); }
      e.count += c.count;
      e.kinds.set(c.kind, (e.kinds.get(c.kind) ?? 0) + c.count);
    });
    const byLayer = new Map<number, CellGroup[]>();
    m.forEach((e) => {
      const list = byLayer.get(e.layerId) ?? [];
      list.push(e);
      byLayer.set(e.layerId, list);
    });
    return byLayer;
  }, [current]);

  const availableLayers = useMemo(
    () => LAYERS.filter((l) => (grouped.get(l.id)?.length ?? 0) > 0),
    [grouped]
  );

  /** Which sub-filter chips to offer per active layer, with their totals on this map. */
  const subFilters = useMemo(() => {
    const out: { layerId: number; groups: { key: string; count: number }[] }[] = [];
    LAYERS.filter((l) => active.includes(l.id)).forEach((l) => {
      const cells = grouped.get(l.id);
      if (!cells) return;
      const tally = new Map<string, number>();
      cells.forEach((c) => c.kinds.forEach((n, kind) => {
        const g = groupOf(l.id, kind);
        if (g) tally.set(g, (tally.get(g) ?? 0) + n);
      }));
      const groups = groupsFor(l.id)
        .filter((g) => (tally.get(g.key) ?? 0) > 0)
        .map((g) => ({ key: g.key, count: tally.get(g.key) ?? 0 }));
      if (groups.length > 1) out.push({ layerId: l.id, groups });
    });
    return out;
  }, [grouped, active]);

  /** Count for a cell after applying that layer's kind filter. */
  const filteredCount = useCallback((layerId: number, kinds: Map<number, number>) => {
    const sel = kindFilter[layerId];
    if (!sel || sel.length === 0) {
      let all = 0;
      kinds.forEach((n) => { all += n; });
      return all;
    }
    let sum = 0;
    kinds.forEach((n, kind) => {
      const g = groupOf(layerId, kind);
      if (g && sel.includes(g)) sum += n;
    });
    return sum;
  }, [kindFilter]);

  const toggleKind = useCallback((layerId: number, key: string) => {
    setKindFilter((f) => {
      const cur = f[layerId] ?? [];
      const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
      return { ...f, [layerId]: next };
    });
    setPinned(null);
  }, []);

  const toggle = useCallback((id: number) => {
    setActive((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));
    setPinned(null);
  }, []);

  useEffect(() => { setPinned(null); setHover(null); }, [mapIdx]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !config || !current) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const cell = positions.cellSize;
    const cos = Math.cos(yaw), sin = Math.sin(yaw);

    const isoR = (rx: number, ry: number): Pt => ({
      x: rx * cos - ry * sin,
      y: (rx * sin + ry * cos) * elev,
    });
    const project = (wx: number, wy: number) =>
      isoR((wx - config.pos_x) / config.scale, (config.pos_y - wy) / config.scale);

    const plate = [isoR(0, 0), isoR(RADAR, 0), isoR(RADAR, RADAR), isoR(0, RADAR)];
    const maxLift = 155;
    const pad = 54;

    // Frame on the DATA (all cells, not just visible layers, so toggling never re-frames),
    // expanded for map context. The plate may bleed off-canvas — better than a small model
    // adrift in empty space.
    const dataPts = (current.cells ?? []).map((c) =>
      project(c.x * cell + cell / 2, c.y * cell + cell / 2));
    const srcPts = dataPts.length > 0 ? dataPts : plate;
    let minX = Math.min(...srcPts.map((p) => p.x)), maxX = Math.max(...srcPts.map((p) => p.x));
    let minY = Math.min(...srcPts.map((p) => p.y)), maxY = Math.max(...srcPts.map((p) => p.y));
    const exW = (maxX - minX) * 0.26 || 1, exH = (maxY - minY) * 0.26 || 1;
    minX -= exW; maxX += exW; minY -= exH; maxY += exH;

    const fit = Math.min(
      (W - pad * 2) / Math.max(maxX - minX, 1),
      (H - pad * 2 - maxLift) / Math.max(maxY - minY, 1)
    ) * zoom;
    const offX = W / 2 - ((minX + maxX) / 2) * fit;
    const offY = H / 2 - ((minY + maxY) / 2) * fit + maxLift * 0.3;
    const place = (p: Pt, lift = 0): Pt => ({ x: p.x * fit + offX, y: p.y * fit + offY - lift });

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, W, H);

    // ---- floor: opaque slab, then the radar clipped onto it
    const platePx = plate.map((p) => place(p));
    const tracePlate = () => {
      ctx.beginPath();
      platePx.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
      ctx.closePath();
    };
    tracePlate();
    const slab = ctx.createLinearGradient(0, platePx[0].y, 0, platePx[2].y);
    slab.addColorStop(0, "#33333a");
    slab.addColorStop(1, "#1d1d22");
    ctx.fillStyle = slab;
    ctx.fill();

    if (radar) {
      ctx.save();
      tracePlate();
      ctx.clip();
      ctx.setTransform(fit * cos, fit * elev * sin, -fit * sin, fit * elev * cos, offX, offY);
      ctx.globalAlpha = 0.9;
      ctx.filter = "grayscale(1) brightness(1.25) contrast(1.05)";
      ctx.drawImage(radar, 0, 0, RADAR, RADAR);
      ctx.filter = "none";
      ctx.restore();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
    }
    tracePlate();
    ctx.strokeStyle = "rgba(255,255,255,0.16)";
    ctx.lineWidth = 1.2;
    ctx.stroke();

    // ---- build pillars
    const shown = LAYERS.filter((l) => active.includes(l.id) && grouped.has(l.id));
    const pillars: Pillar[] = [];

    shown.forEach((layer) => {
      // Heights renormalise to the FILTERED counts, so filtering to e.g. snipers rescales the
      // map to sniper activity instead of leaving every pillar squashed by the unfiltered peak.
      const cells = (grouped.get(layer.id) ?? [])
        .map((c) => ({ c, n: filteredCount(layer.id, c.kinds) }))
        .filter((x) => x.n > 0);
      const peak = cells.reduce((mx, x) => Math.max(mx, x.n), 1);
      cells.forEach(({ c, n }) => {
        const g = cell * 0.08;
        const x0 = c.cx * cell + g, y0 = c.cy * cell + g;
        const x1 = (c.cx + 1) * cell - g, y1 = (c.cy + 1) * cell - g;
        const corners = [project(x0, y0), project(x1, y0), project(x1, y1), project(x0, y1)];
        const intensity = Math.sqrt(n / peak);
        const lift = 7 + Math.pow(intensity, 1.35) * maxLift;
        const sel = kindFilter[layer.id];
        pillars.push({
          layerId: layer.id,
          cx: c.cx, cy: c.cy,
          count: n,
          kinds: Array.from(c.kinds.entries())
            .filter(([kind]) => {
              if (!sel || sel.length === 0) return true;
              const g2 = groupOf(layer.id, kind);
              return !!g2 && sel.includes(g2);
            })
            .map(([kind, count]) => ({ kind, count }))
            .sort((a, b) => b.count - a.count),
          screenBase: corners.map((p) => place(p, 0)),
          screenTop: corners.map((p) => place(p, lift)),
          depth: corners.reduce((s, p) => s + p.y, 0) / 4,
          rgb: mix(layer.lo, layer.rgb, Math.min(1, intensity * 1.15)),
          t: intensity,
        });
      });
    });

    pillars.sort((a, b) => a.depth - b.depth);
    pillarsRef.current = pillars;

    const focus = pinned ?? hover;
    const isFocused = (p: Pillar) =>
      !!focus && focus.layerId === p.layerId && focus.cx === p.cx && focus.cy === p.cy;

    pillars.forEach((p) => {
      const { screenBase: base, screenTop: top, rgb, t: intensity } = p;
      const on = isFocused(p);
      const dim = !!pinned && !on;
      const alpha = dim ? 0.42 : 1;
      const shade = (f: number, a = alpha) =>
        `rgba(${Math.round(rgb[0] * f)},${Math.round(rgb[1] * f)},${Math.round(rgb[2] * f)},${a})`;

      // soft contact shadow — what makes a pillar look seated rather than pasted on
      if (!dim) {
        ctx.save();
        ctx.shadowColor = "rgba(0,0,0,0.62)";
        ctx.shadowBlur = 13;
        ctx.shadowOffsetY = 5;
        ctx.beginPath();
        base.forEach((q, i) => (i === 0 ? ctx.moveTo(q.x, q.y) : ctx.lineTo(q.x, q.y)));
        ctx.closePath();
        ctx.fillStyle = "rgba(0,0,0,0.5)";
        ctx.fill();
        ctx.restore();
      }

      // Side walls, camera-facing only, each with a vertical gradient: lit at the top,
      // occluded toward the floor. Flat fills are what made this read as "drawn".
      for (let i = 0; i < 4; i++) {
        const a = base[i], b = base[(i + 1) % 4];
        const at = top[i], bt = top[(i + 1) % 4];
        if (-(b.x - a.x) <= 0) continue;
        const lit = b.y - a.y > 0 ? 0.72 : 0.5;
        const grad = ctx.createLinearGradient(0, Math.min(at.y, bt.y), 0, Math.max(a.y, b.y));
        grad.addColorStop(0, shade(lit * 1.12));
        grad.addColorStop(1, shade(lit * 0.55));
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.lineTo(bt.x, bt.y);
        ctx.lineTo(at.x, at.y);
        ctx.closePath();
        ctx.fillStyle = grad;
        ctx.fill();
      }

      // top face + rim light
      ctx.beginPath();
      top.forEach((q, i) => (i === 0 ? ctx.moveTo(q.x, q.y) : ctx.lineTo(q.x, q.y)));
      ctx.closePath();
      if (!dim && (intensity > 0.82 || on)) {
        ctx.save();
        ctx.shadowColor = shade(1, 0.95);
        ctx.shadowBlur = on ? 26 : 16;
        ctx.fillStyle = shade(1);
        ctx.fill();
        ctx.restore();
      }
      ctx.fillStyle = on ? "rgba(255,255,255,0.95)" : shade(1.06);
      ctx.fill();
      ctx.strokeStyle = on ? "rgba(255,255,255,1)" : shade(1.35, alpha);
      ctx.lineWidth = on ? 1.8 : 1;
      ctx.stroke();
    });
  }, [positions, current, grouped, active, yaw, elev, zoom, config, radar, hover, pinned, kindFilter, filteredCount]);

  // ---- pointer: orbit, zoom, and hit-test for inspection ----

  const pickAt = (clientX: number, clientY: number): Pillar | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const r = canvas.getBoundingClientRect();
    const p = {
      x: ((clientX - r.left) / r.width) * W,
      y: ((clientY - r.top) / r.height) * H,
    };
    // Front-most first: pillars are painted far -> near, so walk backwards.
    // Test the whole visible silhouette (top face + the two camera-facing walls), not just
    // the top face — on a tall pillar the top is a tiny diamond and was near-impossible to hit.
    const list = pillarsRef.current;
    for (let i = list.length - 1; i >= 0; i--) {
      const q = list[i];
      if (inPoly(p, q.screenTop)) return q;
      for (let k = 0; k < 4; k++) {
        const a = q.screenBase[k], b = q.screenBase[(k + 1) % 4];
        if (-(b.x - a.x) <= 0) continue; // back-facing wall, not drawn
        const at = q.screenTop[k], bt = q.screenTop[(k + 1) % 4];
        if (inPoly(p, [a, b, bt, at])) return q;
      }
    }
    return null;
  };

  const onDown = (e: React.PointerEvent) => {
    drag.current = { x: e.clientX, y: e.clientY, moved: false };
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };

  const onMove = (e: React.PointerEvent) => {
    if (drag.current) {
      const dx = e.clientX - drag.current.x;
      const dy = e.clientY - drag.current.y;
      if (Math.abs(dx) + Math.abs(dy) > 2) drag.current.moved = true;
      drag.current.x = e.clientX;
      drag.current.y = e.clientY;
      setYaw((v) => v + dx * 0.006);
      setElev((v) => Math.min(1.1, Math.max(0.16, v - dy * 0.004)));
      return;
    }
    // Coalesce to one hit-test per frame, and only touch state when the hovered pillar
    // actually CHANGES — a redraw per mouse-move pixel is what made this feel glitchy.
    const { clientX, clientY } = e;
    if (hoverRaf.current) return;
    hoverRaf.current = requestAnimationFrame(() => {
      hoverRaf.current = 0;
      const hit = pickAt(clientX, clientY);
      setHover((cur) => {
        const same = (!cur && !hit) ||
          (!!cur && !!hit && cur.layerId === hit.layerId && cur.cx === hit.cx && cur.cy === hit.cy);
        return same ? cur : hit;
      });
    });
  };

  const onUp = (e: React.PointerEvent) => {
    const wasDrag = drag.current?.moved ?? false;
    drag.current = null;
    // A click (not a drag) pins the breakdown so it survives the pointer leaving.
    if (!wasDrag) {
      const hit = pickAt(e.clientX, e.clientY);
      setPinned((cur) =>
        hit && cur && cur.layerId === hit.layerId && cur.cx === hit.cx && cur.cy === hit.cy
          ? null
          : hit);
    }
  };

  const onLeave = () => {
    drag.current = null;
    if (hoverRaf.current) { cancelAnimationFrame(hoverRaf.current); hoverRaf.current = 0; }
    setHover(null);
  };

  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    setZoom((z) => Math.min(2.6, Math.max(0.55, z - e.deltaY * 0.0012)));
  };

  if (!current || !config) return null;

  const totals = new Map(
    LAYERS.map((l) => [l.id, (grouped.get(l.id) ?? []).reduce((s, c) => s + c.count, 0)])
  );

  const focus = pinned ?? hover;
  const focusLayer = focus ? LAYER_BY_ID.get(focus.layerId) : undefined;
  const kindLabel = (layerId: number, kind: number) =>
    layerId === 4 ? (UTIL_KIND[kind] ?? `#${kind}`) : (WEAPON_NAMES[kind] ?? `#${kind}`);

  return (
    <div className={styles.posWrap}>
      <div className={styles.title}>
        {t("wrapped.positions.title", { map: short(current.mapName) })}
      </div>
      {renderable.length > 1 && (
        <div className={styles.posMaps}>
          {renderable.map((m, i) => (
            <button
              key={m.mapName}
              className={`${styles.posMapBtn} ${i === mapIdx ? styles.posMapBtnOn : ""}`}
              onClick={() => setMapIdx(i)}
              aria-pressed={i === mapIdx}
            >
              {short(m.mapName)}
            </button>
          ))}
        </div>
      )}

      <div className={styles.posLayers}>
        {availableLayers.map((l) => {
          const on = active.includes(l.id);
          return (
            <button
              key={l.id}
              className={`${styles.posChip} ${on ? styles.posChipOn : ""}`}
              style={on ? { borderColor: `rgb(${l.rgb.join(",")})`, color: `rgb(${l.rgb.join(",")})` } : undefined}
              onClick={() => toggle(l.id)}
              aria-pressed={on}
            >
              <span className={styles.posDot} style={{ background: `rgb(${l.rgb.join(",")})` }} />
              {t(`wrapped.positions.layer.${l.key}`)}
              <b>{totals.get(l.id)}</b>
            </button>
          );
        })}
      </div>

      {subFilters.map(({ layerId, groups }) => {
        const layer = LAYER_BY_ID.get(layerId)!;
        const sel = kindFilter[layerId] ?? [];
        return (
          <div key={layerId} className={styles.posSubRow}>
            <span className={styles.posSubLabel}>{t(`wrapped.positions.layer.${layer.key}`)}</span>
            {groups.map((g) => {
              const on = sel.length === 0 || sel.includes(g.key);
              return (
                <button
                  key={g.key}
                  className={`${styles.posSubChip} ${sel.includes(g.key) ? styles.posSubChipOn : ""}`}
                  onClick={() => toggleKind(layerId, g.key)}
                  aria-pressed={sel.includes(g.key)}
                  style={on ? undefined : { opacity: 0.4 }}
                >
                  {t(`wrapped.positions.kind.${g.key}`)}
                  <b>{g.count}</b>
                </button>
              );
            })}
            {sel.length > 0 && (
              <button
                className={styles.posSubClear}
                onClick={() => { setKindFilter((f) => ({ ...f, [layerId]: [] })); setPinned(null); }}
              >
                {t("wrapped.positions.clearFilter")}
              </button>
            )}
          </div>
        );
      })}

      <div className={styles.posStage}>
        <canvas
          ref={canvasRef}
          width={W}
          height={H}
          className={styles.posCanvas}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerLeave={onLeave}
          onWheel={onWheel}
          role="img"
          aria-label={t("wrapped.positions.title", { map: short(current.mapName) })}
        />

        {focus && focusLayer && (
          <div className={styles.posInspect} data-no-advance>
            <div className={styles.posInspectHead}>
              <span className={styles.posDot} style={{ background: `rgb(${focusLayer.rgb.join(",")})` }} />
              {t(`wrapped.positions.layer.${focusLayer.key}`)}
              <b>{focus.count}</b>
              {pinned && <span className={styles.posPinned}>{t("wrapped.positions.pinned")}</span>}
            </div>
            <div className={styles.posInspectRows}>
              {focus.kinds.slice(0, 6).map((k) => (
                <div key={k.kind} className={styles.posInspectRow}>
                  <span className={styles.posInspectName}>{kindLabel(focus.layerId, k.kind)}</span>
                  <span className={styles.posInspectBar}>
                    <span
                      style={{
                        width: `${(k.count / focus.count) * 100}%`,
                        background: `rgb(${focusLayer.rgb.join(",")})`,
                      }}
                    />
                  </span>
                  <span className={styles.posInspectNum}>{k.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={styles.posHint}>
          {focus ? t("wrapped.positions.hintPinned") : t("wrapped.positions.hint")}
        </div>
      </div>
    </div>
  );
};

export default WrappedPositionMap;
